/**
 * One trader's own page: who they are, and what they have.
 *
 * THE QUESTION IT ANSWERS, in the owner's words: "I would like to add the
 * possibility of seeing a user profile with its listings, so I could easily
 * check what my neighbour has for sale." Browse answers "what is near me". This
 * answers "what does THIS person have", which is what somebody asks after one
 * good trade — and until now the only way to ask it was to scroll browse hoping
 * the same name came round again.
 *
 * WHAT IT DELIBERATELY DOES NOT SAY. No town, no neighbourhood, no postcode, not
 * even a vague one. A page about a person is precisely where somebody would be
 * tempted to add "lives in Murcia", and the project's answer is that a place
 * name cannot tell you whether you can go and get the card — a distance can —
 * while being personal data about a stranger. So: a coarsened distance, and
 * nothing else about where they are.
 *
 * PUBLIC, like browse and like a listing, because their listings already are.
 * A signed-out visitor following a shared link sees the cards and the history.
 * What they do not see is a distance, because there is nothing to measure from.
 */
import React from "react";
import { api, suggestionNameFor } from "./api.jsx";
import { useApp, usePagedList } from "./store.jsx";
import { BackLink, LoadMore, ListingGridSkeleton, Linked, CardmarketMark, cardmarketProfileUrl } from "./chrome.jsx";
import { GAMES, CARD_LANGUAGES, CARD_LANGUAGE_NAMES, tradingName, tradingHandle } from "./data.jsx";
import { hoursSummary } from "./hours.jsx";
import { CardAutocomplete } from "./autocomplete.jsx";

const { Button, Badge, Card, Icon, Tag, TraderRow, ListingCard, EmptyState, Select, Checkbox } = window.CardboardmarketDesignSystem_2a3239;

/**
 * Searching one seller's shelf.
 *
 * "A seller page's listings should also be filterable. I don't mean by radius,
 * but we should be able to search by card name/set, foil or no foil, games,
 * languages, only cards on your wants lists, only cards with a photograph."
 *
 * NOT BROWSE'S RAIL, reused. That one carries a radius, and a radius is the one
 * filter this page must not have: you are looking at a person, not at your
 * surroundings, and narrowing their shelf by your own distance would hide their
 * cards for a reason that has nothing to do with them. It also carries a sort
 * and tab counts, neither of which a single shelf has.
 *
 * The SERVER does the filtering, not this. A client that narrowed the page it
 * already had would show "nothing matches" the moment a seller had more cards
 * than one page holds, and the filter would quietly mean "of the first fifty".
 */
function ShelfFilters({ value, onChange, t }) {
  /*
   * THE DESIGN SYSTEM'S CONTROLS DO NOT ALL HAND BACK THE SAME THING, and this
   * cost an hour. `Select` calls `onChange(e.target.value)` and `Checkbox` calls
   * `onChange(e.target.checked)` — the VALUE, already unwrapped. `Input` spreads
   * its props straight onto the element, so that one receives the raw event.
   *
   * Written as `e.target.value` against a Select, the handler stores `undefined`
   * on a string, React renders the control back to empty, and the page looks
   * like the filter simply does nothing. Nothing throws.
   */
  const set = (patch) => onChange({ ...value, ...patch });
  const any = { value: '', label: t("Any") };
  return (
    <div style={{
      display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'flex-end',
      margin: '0 0 var(--space-5)',
    }}>
      {/*
        BROWSE'S TYPEAHEAD, so a card is found by its name in any language we
        hold (migration 157): typing "Garchomp ex de Cintia" offers the card,
        and picking it searches its English name, which is what this shelf's
        search reads. Typing alone still searches what was typed. The list is
        the catalogue's, narrowed to the game chosen here; see trader-search
        in e2e/tests/card-names.spec.ts.
      */}
      <CardAutocomplete
        boxStyle={{ flex: '1 1 220px' }}
        label={t("Search their cards")}
        icon="search"
        placeholder={t("Card or set")}
        value={value.q}
        game={value.game || undefined}
        onChange={(q) => set({ q, card: '' })}
        /* Picked: the box keeps the name it was found by, and `card` carries the
           English name the shelf's search reads — browse's `pickCard`, the same rule. */
        onPick={(item) => set({
          q: suggestionNameFor(item),
          card: suggestionNameFor(item) !== item.name ? item.name : '',
        })}
      />
      <Select
        wrapperStyle={{ flex: '0 1 150px' }}
        label={t("Game")}
        value={value.game}
        onChange={(game) => set({ game })}
        options={[{ value: '', label: t("All games") }].concat(GAMES.map((g) => ({ value: g.id, label: g.label })))}
      />
      <Select
        wrapperStyle={{ flex: '0 1 150px' }}
        label={t("Card language")}
        value={value.language}
        onChange={(language) => set({ language })}
        options={[{ value: '', label: t("Any language") }].concat(
          CARD_LANGUAGES.map((code) => ({ value: code, label: t(CARD_LANGUAGE_NAMES[code] || code) })),
        )}
      />
      <Select
        wrapperStyle={{ flex: '0 1 140px' }}
        label={t("Sale or trade")}
        value={value.mode}
        onChange={(mode) => set({ mode })}
        options={[any, { value: 'sale', label: t("For sale") }, { value: 'trade', label: t("For trade") }]}
      />
      <Select
        wrapperStyle={{ flex: '0 1 140px' }}
        label={t("Foil / Holo / Shiny")}
        value={value.foil}
        onChange={(foil) => set({ foil })}
        options={[any, { value: 'only', label: t("Foil only") }, { value: 'none', label: t("No foil") }]}
      />
      <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', paddingBottom: 6 }}>
        <Checkbox
          label={t("Only cards I want")}
          checked={value.onlyWants}
          onChange={(onlyWants) => set({ onlyWants })}
        />
        <Checkbox
          label={t("Only cards with a photo")}
          checked={value.withPhotos}
          onChange={(withPhotos) => set({ withPhotos })}
        />
      </div>
    </div>
  );
}

/** Only what was actually asked for reaches the query string. */
function shelfQuery(f) {
  // A picked card is searched by its English name (`card`), whatever the box shows.
  const text = (f.card || f.q).trim();
  return {
    ...(text ? { q: text } : {}),
    ...(f.game ? { game: f.game } : {}),
    ...(f.language ? { language: f.language } : {}),
    ...(f.mode ? { mode: f.mode } : {}),
    ...(f.foil ? { foil: f.foil } : {}),
    ...(f.onlyWants ? { onlyWants: true } : {}),
    ...(f.withPhotos ? { withPhotos: true } : {}),
  };
}

const NO_FILTERS = { q: '', card: '', game: '', language: '', mode: '', foil: '', onlyWants: false, withPhotos: false };

/*
 * THE SHELF'S FILTERS LIVE IN THE LINK (owner, 2026-10-22), in browse's own
 * keys, so a reload or a shared link opens the same narrowed shelf. Anything
 * the shelf does not know is that filter off.
 *
 * EXCEPT THE GAME FOR A SHOP. Working as a shop, this screen sits inside the
 * back office (EMBEDDED in shop-dash.jsx), whose game scope is the same `game`
 * key — written here, the shelf's game would re-scope the shop's stock and
 * requests. So there it stays on this screen, and the scope in the link is
 * left as it was.
 */
const first = (v) => [].concat(v ?? [])[0];
function shelfFromLink(route, scoped) {
  const game = scoped ? '' : first(route.game);
  const language = first(route.language);
  return {
    q: typeof route.q === 'string' ? route.q : '',
    card: typeof route.card === 'string' ? route.card : '',
    game: GAMES.some((g) => g.id === game) ? game : '',
    language: CARD_LANGUAGES.includes(language) ? language : '',
    mode: route.mode === 'sale' || route.mode === 'trade' ? route.mode : '',
    foil: route.foil === 'only' || route.foil === 'none' ? route.foil : '',
    onlyWants: route.only === 'wants',
    withPhotos: route.photos === '1',
  };
}
function shelfLink(f, scoped, route) {
  return {
    q: f.q || undefined,
    card: f.card || undefined,
    game: scoped ? route.game : (f.game || undefined),
    language: f.language || undefined,
    mode: f.mode || undefined,
    foil: f.foil || undefined,
    only: f.onlyWants ? 'wants' : undefined,
    photos: f.withPhotos ? '1' : undefined,
  };
}

export function TraderScreen() {
  const { route, navigate, go, messageTrader, point, trader: viewer, booting, locationsReady, t } = useApp();
  const key = route.id;
  const scoped = viewer?.kind === 'shop';

  const [trader, setTrader] = React.useState(null);
  const [missing, setMissing] = React.useState(false);
  const [listings, setListings] = React.useState([]);
  const [state, setState] = React.useState('loading');
  const [cursor, setCursor] = React.useState(null);
  const [more, setMore] = React.useState(false);
  /* So a "Show more" cannot cross a change of filter. See `usePagedList`. */
  const shelfPages = usePagedList();
  const [filters, setFiltersHere] = React.useState(() => shelfFromLink(route, scoped));
  /* On screen and into the link, replaced as browse's filters are. Every other key of the address is kept. */
  const setFilters = (next) => {
    setFiltersHere(next);
    const { name, ...rest } = route;
    navigate(name, { ...rest, ...shelfLink(next, scoped, route) }, { replace: true, keepScroll: true });
  };
  /*
   * THE TYPED TEXT, HELD BACK. Every other filter is a single deliberate choice
   * and asks the moment it changes; a search box would ask once per keystroke.
   *
   * 250 ms, and it applies only to `q` — changing the game while a search is
   * settling must not wait for it. Held as a SEPARATE piece of state rather than
   * by delaying the request, so the box itself stays perfectly responsive: what
   * lags is the asking, never the typing.
   */
  // Settled already on arrival, or a linked search would ask twice: once without it.
  const [settledQ, setSettledQ] = React.useState(filters.q);
  React.useEffect(() => {
    const id = setTimeout(() => setSettledQ(filters.q), 250);
    return () => clearTimeout(id);
  }, [filters.q]);
  const asked = React.useMemo(
    () => shelfQuery({ ...filters, q: settledQ }),
    [settledQ, filters.card, filters.game, filters.language, filters.mode, filters.foil, filters.onlyWants, filters.withPhotos],
  );
  /*
   * A STABLE KEY for the effect below. `asked` is a fresh object on every
   * render, so depending on it by identity refetches forever — the same trap as
   * the coordinate array, one layer up.
   */
  const askedKey = JSON.stringify(asked);

  /*
   * Coordinates as primitives in the dependency list. `point` is an array, so
   * depending on it by identity refetches on every render that happens to build
   * a new one — the same trap `ListingScreen` documents above its own effect.
   */
  const lat = point ? point[0] : null;
  const lng = point ? point[1] : null;

  const where = lat != null && lng != null ? { lat, lng } : undefined;

  /*
   * The HEADER, which no filter touches. Separated from the shelf below so that
   * narrowing the cards does not blank the person's name and trade count and
   * paint them back in — the page would flicker its own identity on every
   * keystroke, and a header that comes and goes reads as a page still loading.
   */
  React.useEffect(() => {
    if (!key) return;
    let live = true;
    setTrader(null); setMissing(false);
    api.trader(key, where)
      .then((r) => { if (live) setTrader(r.trader); })
      .catch(() => { if (live) setMissing(true); });
    return () => { live = false; };
  }, [key, lat, lng]);

  React.useEffect(() => {
    /*
     * NOT BEFORE WE KNOW WHERE THE VIEWER IS, as browse waits (`locationsReady`
     * in store.jsx): asked at once, the shelf went out without a point and
     * again a moment later with one, so every arrival cost two requests.
     */
    if (!key || booting || !locationsReady) return;
    let live = true;
    const shown = shelfPages.reload();
    setState('loading'); setListings([]); setCursor(null);
    api.traderListings(key, { ...asked, ...(where || {}) })
      .then((r) => {
        if (!live) return;
        setListings(r.listings.map(api.toListingView));
        setCursor(r.nextCursor ?? null);
        shown();
        setState('ready');
      })
      /*
       * A failed shelf is not an empty one. This screen's whole promise is "here
       * is everything they have", so painting nothing after a failed request
       * would be the screen telling a lie about somebody else's inventory.
       */
      .catch(() => { if (live) setState('error'); });
    return () => { live = false; };
  }, [key, lat, lng, askedKey, booting, locationsReady]);

  const loadMore = async () => {
    const belongs = shelfPages.page();
    if (!cursor || more || !belongs) return;
    setMore(true);
    try {
      // The filters travel with the page. Without them the second page is the
      // unfiltered shelf, appended under a filtered first one.
      const r = await api.traderListings(key, { ...asked, cursor, ...(where || {}) });
      // Asked of a shelf that has since been asked again: not this shelf's page.
      if (!belongs()) return;
      setListings((prev) => prev.concat(r.listings.map(api.toListingView)));
      setCursor(r.nextCursor ?? null);
    } finally { setMore(false); }
  };

  if (missing) {
    return (
      <>
        <BackLink label={t("Back to browse")} />
        <EmptyState icon="user" title={t("No trader by that name")}
          description={t("The link may be old, or they may have closed their account.")}
          action={<Button onClick={() => go('browse')}>{t("Browse nearby cards")}</Button>} />
      </>
    );
  }

  return (
    <>
      <BackLink />
      {trader ? (
        <>
          {/*
            THE NAME IS A HEADING, and it is a heading because of a bug this
            screenshot caught. `TraderRow` renders its name with
            `overflow: hidden; white-space: nowrap`, and on a phone the row also
            holds a distance, a trade count, a verified badge and a button. A
            shop called "Dados y Dragones" was clipped to nothing — the single
            most important word on the page, invisible on the device most people
            read it on, with no error anywhere.

            It is also just right. This page is ABOUT somebody, so their name is
            its title rather than a field inside a row, and the document finally
            has an outline.

            THE SHOP'S NAME WINS where there is one. The owner's ask: "a user can
            register its local game store and operate under the store's name."
            Somebody who plays at Dados y Dragones is looking for Dados y
            Dragones, not for whoever registered it.
          */}
          <h1 className="cbm-h1" style={{ fontSize: 'var(--text-2xl)', margin: '0 0 var(--space-3)' }}>
            {tradingName(trader)}
          </h1>
          <TraderRow
            /*
             * `initials` explicitly, and no `name`. The row draws initials from
             * the name when it is given one, so passing the name would put it
             * back in the cramped span the heading above exists to escape.
             */
            initials={tradingName(trader).slice(0, 2).toUpperCase()}
            handle={tradingHandle(trader) ? '@' + tradingHandle(trader) : undefined}
            /*
             * `km` is what the design system's row takes, and the API sends
             * metres. Null below the floor and undefined where the reader has no
             * point of their own — the row renders the floor label for the first
             * and nothing at all for the second, which are different answers and
             * must stay different. See the distance decision in CLAUDE.md.
             */
            km={trader.distance.known ? (trader.distance.metres == null ? null : trader.distance.metres / 1000) : undefined}
            distanceFloorLabel={t("less than 1 km")}
            // Zero is not missing data — it is "new here", which is the most
            // useful thing a stranger can know before agreeing to meet somebody.
            trades={trader.completedTrades}
            tradesLabel={t("{count} trades", { count: trader.completedTrades })}
            /*
             * IT STOPS BEING A LIE. `TraderRow` has carried a `verified` prop
             * since the design system was written and every call site in this
             * product passed `false`, because there was nothing behind it.
             * A checked shop is what there is now, and nothing else sets it.
             */
            verified={Boolean(trader.verified)}
            verifiedLabel={t("Verified store")}
            action={trader.isYou
              ? <Button size="sm" variant="outline" onClick={() => go('you')}>{t("Your listings")}</Button>
              : <Button size="sm" variant="outline" iconLeft="message-circle"
                  onClick={() => messageTrader(trader.id, trader.displayName)}>{t("Message")}</Button>}
          />
          {/*
            WHERE ELSE THEY SELL, which a profile is exactly the page for.

            Reported: "I can set the Cardmarket handle but I don't see it in the
            listing page or the profile page." It was stored, settable and shown
            nowhere — a field that does nothing.

            A plain link, with no reveal step and no visibility switch, because
            it addresses a page anybody can already read; storing it is the
            consent. That is the whole reason it is not beside WhatsApp and
            Telegram, which hide a phone number and a personal account behind a
            tap that needs an account.
          */}
          {trader.cardmarketUsername ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <Icon name="external-link" size={16} />
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                {t("Also sells on")} <CardmarketMark />
              </span>
              <a
                className="cbm-textlink"
                href={cardmarketProfileUrl(trader.cardmarketUsername, trader.cardmarketGame)}
                target="_blank" rel="noopener noreferrer nofollow"
                style={{ font: 'var(--type-ui)', fontSize: 'var(--text-sm)' }}
              >
                {trader.cardmarketUsername}
              </a>
            </div>
          ) : null}
          {/*
            THE ADDRESS, WHICH EXISTS ONLY HERE. Every other screen in this
            product refuses to name a place, and that rule is untouched: this is
            a business address on a business's own page, which the owner put
            plainly — "I want to advertise where I'm located so people that play
            at my store can recognize me and other people can easily find me."

            The map link is a plain URL and costs nothing until it is tapped.
          */}
          {trader.shop ? (
            /*
              THE DESIGN SYSTEM'S OWN `ShopAddressCard`, rather than my version
              of it.

              What shipped here was a degraded copy of this: a hairline
              rectangle, the address, then three links in a row set in the meta
              face in `--ink-700`, with no icon and no indent — which is why it
              read as a code block and was reported as "a bit ugly". Every part
              that made it look wrong was a part of the design that had been
              dropped.

              So: a Card, the pin beside the address, and the links beneath it
              in `--text-link`, indented to hang under the address rather than
              under the icon. `ui_kits/app/ShopProfileScreen.jsx`.

              The address block exists ONLY here, and the design says why:
              everywhere else this product refuses to name a place, because a
              trader's town is personal data that answers no question a buyer
              has. A shop is the opposite — a business asking to be found, whose
              address is already on its own door.
            */
            <Card style={{ marginTop: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/*
                WHAT THE SHOP SAID ABOUT ITSELF, above the address it is found at.
                
                The line, the games and the week of opening hours are asked for
                in the application; showing none of them would be a form asking
                for work nobody ever sees. The hours matter most of the three —
                they are what somebody standing outside the door needs, and a
                trade arranged here has to fall inside them.
              */}
              {trader.shop.about ? (
                <span style={{ font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                  {trader.shop.about}
                </span>
              ) : null}
              {(trader.shop.games || []).length ? (
                <div className="cbm-wrap">
                  {GAMES.filter((g) => trader.shop.games.includes(g.id)).map((g) => (
                    <Tag key={g.id}>{g.label}</Tag>
                  ))}
                </div>
              ) : null}
              {hoursSummary(trader.shop.hours, t) ? (
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <Icon name="clock" size={16} style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1, font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
                    {hoursSummary(trader.shop.hours, t)}
                  </span>
                </div>
              ) : null}
              {trader.shop.counter ? (
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <Icon name="store" size={16} style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1, font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
                    {t("You can meet here")}
                  </span>
                </div>
              ) : null}
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <Icon name="map-pin" size={16} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>
                  {trader.shop.address}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', font: 'var(--type-meta)', marginLeft: 26 }}>
                <a href={mapsLink(trader.shop)} target="_blank" rel="noopener noreferrer"
                   style={{ color: 'var(--text-link)' }}>{t("Open in maps")}</a>
                {trader.shop.phone ? (
                  /* A real tel: link — on the device most people open this on, a
                     phone number is a button. */
                  <a href={`tel:${trader.shop.phone.replace(/\s+/g, '')}`}
                     style={{ color: 'var(--text-link)' }}>{trader.shop.phone}</a>
                ) : null}
                {trader.shop.website ? (
                  <a href={trader.shop.website} target="_blank" rel="noopener noreferrer"
                     style={{ color: 'var(--text-link)' }}>
                    {trader.shop.website.replace(/^https?:\/\//, '')}
                  </a>
                ) : null}
              </div>
            </Card>
          ) : null}

          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap', margin: 'var(--space-4) 0 var(--space-6)' }}>
            {/* The month, never the day. "Trading here since March" is the trust
                signal; the exact date is a fact about somebody's life that
                answers no question a buyer has. */}
            <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
              {t("Trading here since {month}", { month: monthName(trader.since, t) })}
            </span>
            {/* Shown rather than explained. The reasoning for publishing this at
                all is in reputation.ts: it fires only when reports are out of
                proportion to trades, so it is a fact rather than an accusation. */}
            {trader.flagged ? <Badge tone="caution">{t("Reported by other traders")}</Badge> : null}
          </div>
        </>
      ) : null}

      {/* Only where there is something to search. A filter rail above three cards
          is furniture, and above none it is an insult. */}
      {trader && trader.listingCount > 0 ? (
        <ShelfFilters value={filters} onChange={setFilters} t={t} />
      ) : null}

      {state === 'ready' ? (
        listings.length ? (
          <>
            <div className="cbm-grid-listings">
              {listings.map((l) => (
                <Linked key={l.id} to={{ name: 'listing', id: l.id }}>
                  <ListingCard {...api.listingCardProps(l, t)} />
                </Linked>
              ))}
            </div>
            <LoadMore cursor={cursor} busy={more} onClick={loadMore} />
          </>
        ) : (
          /*
           * TWO DIFFERENT EMPTIES, and telling them apart is the whole point.
           * "They have nothing" and "nothing here matches what you asked" lead
           * to opposite next moves: message them, or widen the search. A single
           * message would send half the readers the wrong way.
           */
          Object.keys(asked).length ? (
            <EmptyState icon="search" title={t("Nothing here matches that")}
              description={t("They have cards up, just none matching this search. Try fewer filters.")}
              action={<Button variant="outline" onClick={() => setFilters(NO_FILTERS)}>{t("Clear the filters")}</Button>} />
          ) : (
            <EmptyState icon="layers" title={t("Nothing for sale right now")}
              description={t("They have no cards up at the moment. Message them if you are after something in particular.")} />
          )
        )
      ) : state === 'error' ? (
        <EmptyState icon="alert-triangle" title={t("Their cards could not be loaded")}
          description={t("Something went wrong on our side. Try again in a moment.")} />
      ) : (
        // The grid's own shape while it is in flight, for the same reason every
        // other grid holds one: an empty state here would be a claim about
        // somebody else's inventory, made before we had asked.
        <ListingGridSkeleton label={t("Loading their cards.")} />
      )}
    </>
  );
}

/**
 * A map, by coordinates rather than by name.
 *
 * The same shape the meetup link uses: a plain URL, no key, no bill, and nothing
 * sent anywhere until somebody taps it. Coordinates rather than the address
 * string because a name matches the wrong branch of a chain and a coordinate
 * cannot — see places.test.ts, which pins exactly that.
 */
function mapsLink(shop) {
  const q = `${shop.point.lat},${shop.point.lng}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

/**
 * "2026-03" as a month somebody reads.
 *
 * The API sends the month and not a date, so there is nothing here to round off
 * or accidentally render as a day. The names are translated rather than taken
 * from `Intl`, because the interface's language and the browser's are two
 * different questions and this product answers the first.
 */
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function monthName(since, t) {
  const [year, month] = String(since || '').split('-');
  const name = MONTHS[Number(month) - 1];
  if (!name || !year) return String(since || '');
  return `${t(name)} ${year}`;
}
